import axios from 'axios'

// Priority: 1) Runtime config (Cloud Run env) 2) Vite build-time 3) localhost
declare global { interface Window { __ENV__?: { VITE_API_URL?: string } } }
const API_BASE = (window.__ENV__?.VITE_API_URL) || import.meta.env.VITE_API_URL || 'http://localhost:8000'

const api = axios.create({ baseURL: API_BASE })

// Add user isolation via X-Client-ID + auth token
api.interceptors.request.use((config) => {
  let clientId = localStorage.getItem('easy_market_client_id')
  if (!clientId) {
    clientId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36)
    localStorage.setItem('easy_market_client_id', clientId)
  }
  config.headers['X-Client-ID'] = clientId

  // In guest mode, never send auth even if a stale token remains in storage.
  const isGuest = localStorage.getItem('easy_market_guest') === 'true'

  // Attach JWT token only for authenticated mode.
  const token = localStorage.getItem('easy_market_token')
  if (token && !isGuest) {
    config.headers['Authorization'] = `Bearer ${token}`
  }

  return config
})


// ── Types ────────────────────────────────────────────────────
export interface Ingredient {
  id: string
  canonical_name: string
  preferred_unit: string
  category?: string
}

export interface RecipeIngredient {
  id: string
  ingredient_id?: string
  name: string
  quantity: number
  unit: string
}

export interface Recipe {
  id: string
  name: string
  servings: number
  instructions?: string
  is_personal: boolean
  source: string
  category?: string
  recipe_ingredients: RecipeIngredient[]
}

export interface InventoryItem {
  id: string
  ingredient_id?: string
  ingredient_name: string
  quantity: number
  unit: string
  expiry_date?: string
  added_at: string
}

export interface ShoppingListItem {
  id: string
  ingredient_id?: string
  name: string
  required_quantity: number
  unit: string
  checked: boolean
  owned: boolean
  note?: string
  source_recipe?: string
}

export interface ShoppingList {
  id: string
  name: string
  created_at: string
  status: string
  nb_persons: number
  duration_days: number
  meals_per_day: number
  items: ShoppingListItem[]
  recipes?: Recipe[]
}

export interface Dessert {
  recipe_id: string
  nb_repetitions: number
  nb_persons: number
}

export interface GenerateListRequest {
  name: string
  recipe_ids: string[]
  desserts: Dessert[]
  nb_persons: number
  duration_days: number
  meals_per_day: number
}

export interface Profile {
  nb_persons: number
  meals_per_day: number
}

// ── Recipes ──────────────────────────────────────────────────
export const recipesApi = {
  list: (q = '', category = '', personalOnly = false) =>
    api.get<Recipe[]>('/api/recipes', { params: { q, category, personal_only: personalOnly } }).then(r => r.data),
  get: (id: string) => api.get<Recipe>(`/api/recipes/${id}`).then(r => r.data),
  create: (data: Partial<Recipe> & { ingredients?: { ingredient_name: string; quantity: number; unit: string }[] }) =>
    api.post<Recipe>('/api/recipes', data).then(r => r.data),
  delete: (id: string) => api.delete(`/api/recipes/${id}`),
  llmSearch: (name: string) => api.get<{ name: string; ingredients: { name: string; quantity_per_portion: number; unit: string }[] }>('/api/recipes/search/llm', { params: { name } }).then(r => r.data),
}

// ── Inventory ────────────────────────────────────────────────
export const inventoryApi = {
  list: () => api.get<InventoryItem[]>('/api/inventory').then(r => r.data),
  add: (data: { ingredient_name: string; quantity: number; unit: string; expiry_date?: string }) =>
    api.post<InventoryItem>('/api/inventory', data).then(r => r.data),
  update: (id: string, data: Partial<InventoryItem>) => api.put<InventoryItem>(`/api/inventory/${id}`, data).then(r => r.data),
  delete: (id: string) => api.delete(`/api/inventory/${id}`),
}

// ── Shopping Lists ───────────────────────────────────────────
export const shoppingApi = {
  list: () => api.get<ShoppingList[]>('/api/shopping').then(r => r.data),
  get: (id: string) => api.get<ShoppingList>(`/api/shopping/${id}`).then(r => r.data),
  generate: (data: GenerateListRequest) => api.post<ShoppingList>('/api/shopping/generate', data).then(r => r.data),
  updateItem: (listId: string, itemId: string, data: Partial<ShoppingListItem>) =>
    api.patch<ShoppingListItem>(`/api/shopping/${listId}/items/${itemId}`, data).then(r => r.data),
  addItem: (listId: string, data: { name: string; required_quantity: number; unit: string; note?: string }) =>
    api.post<ShoppingListItem>(`/api/shopping/${listId}/items`, data).then(r => r.data),
  deleteItem: (listId: string, itemId: string) => api.delete(`/api/shopping/${listId}/items/${itemId}`),
  updateStatus: (listId: string, status: string) =>
    api.patch(`/api/shopping/${listId}/status`, null, { params: { status } }),
  delete: (id: string) => api.delete(`/api/shopping/${id}`),
}

// ── Profile ──────────────────────────────────────────────────
export const profileApi = {
  get: () => api.get<Profile>('/api/profile').then(r => r.data),
  update: (data: Partial<Profile>) => api.put<Profile>('/api/profile', data).then(r => r.data),
}

// ── Ingredients ──────────────────────────────────────────────
export const ingredientsApi = {
  search: (q: string) => api.get<Ingredient[]>('/api/ingredients', { params: { q } }).then(r => r.data),
}

export default api
